import { getTargetLocationProvider } from "@sill/shared/places/target-location-provider";
import type { TargetLocationSuggestion } from "@sill/shared/places/target-location";
import { MapPin } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

const MIN_QUERY = 3;
const DEBOUNCE_MS = 200;
const MAX_SUGGESTIONS = 5;

export interface ServiceAreaFieldProps {
  id: string;
  label: string;
  value: string;
  /** `placeId` is set only when `value` is exactly a picked suggestion's label. */
  onChange: (value: string, placeId?: string) => void;
  placeholder: string;
  required?: boolean;
  maxLength?: number;
}

/**
 * Service-area typeahead over Google Places (cities, neighborhoods, ZIPs, any
 * market). Without `VITE_GOOGLE_MAPS_API_KEY` it is a plain text input. Free
 * text always stays valid: a pick adds a place id, typing drops it.
 */
export function ServiceAreaField({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  maxLength,
}: ServiceAreaFieldProps) {
  const provider = useMemo(() => getTargetLocationProvider(), []);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [lookup, setLookup] = useState<{ query: string; items: TargetLocationSuggestion[] }>({
    query: "",
    items: [],
  });
  // A pick fills the input with its label; that programmatic value must not
  // re-open the list by searching for itself.
  const pickedLabelRef = useRef<string | null>(null);

  const query = value.trim();
  useEffect(() => {
    if (!provider || query.length < MIN_QUERY || pickedLabelRef.current === query) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      provider
        .search(query)
        .then((items) => {
          if (!cancelled) setLookup({ query, items });
        })
        .catch(() => {
          if (!cancelled) setLookup({ query, items: [] });
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [provider, query]);

  const suggestions =
    provider && lookup.query === query ? lookup.items.slice(0, MAX_SUGGESTIONS) : [];
  const expanded = open && suggestions.length > 0;

  function pick(item: TargetLocationSuggestion) {
    pickedLabelRef.current = item.label;
    onChange(item.label, item.place_id);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!expanded) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  }

  return (
    <div className="relative">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <div className="field">
        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          required={required}
          maxLength={maxLength}
          autoComplete="off"
          onChange={(e) => {
            pickedLabelRef.current = null;
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Option mousedown preventDefaults, so a click never blurs first.
            setOpen(false);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          {...(provider
            ? {
                role: "combobox",
                "aria-autocomplete": "list" as const,
                "aria-expanded": expanded,
                "aria-controls": listId,
                "aria-activedescendant": active >= 0 ? `${listId}-${active}` : undefined,
              }
            : {})}
        />
      </div>
      {provider ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={`${label} suggestions`}
          hidden={!expanded}
          className="absolute inset-x-0 z-20 mt-1.5 overflow-hidden rounded-[14px] border border-border bg-card p-1 shadow-card"
        >
          {suggestions.map((item, i) => (
            <li
              key={item.place_id ?? item.label}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(item)}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm text-fg ${
                i === active ? "bg-pro-soft" : ""
              }`}
            >
              <MapPin aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={2} />
              <span className="truncate">{item.label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
